import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAccount } from './AccountContext';
import AuthModal from '../components/AuthModal';

export const FavoritesContext = createContext();

// Wishlist persistente con cuenta (server/services/favoritesService.js).
// Deliberadamente separado de CartContext (localStorage, alimenta el
// checkout real de marketplace) -- ver docs/esganga-master-plan.md sesion de
// hoy. Sin version anonima: el valor real de esto es sobrevivir entre
// dispositivos, algo que localStorage ya no puede dar.
export const FavoritesProvider = ({ children }) => {
    const { user } = useAccount();
    const [favorites, setFavorites] = useState([]);
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const [authOpen, setAuthOpen] = useState(false);

    const refresh = useCallback(() => {
        if (!user) {
            setFavorites([]);
            setFavoriteIds(new Set());
            return;
        }
        fetch('/api/favorites/mine', { credentials: 'include' })
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error('fetch-failed'))))
            .then((data) => {
                const list = data.favorites || [];
                setFavorites(list);
                setFavoriteIds(new Set(list.map((deal) => deal.id)));
            })
            .catch(() => {});
    }, [user]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const toggleFavorite = useCallback((deal) => {
        if (!user) {
            setAuthOpen(true);
            return;
        }

        const dealId = deal.id;
        const isFavorite = favoriteIds.has(dealId);

        // Optimista: la UI cambia al instante, se revierte solo si el
        // servidor falla -- mismo espiritu que el resto de acciones rapidas
        // del sitio (toasts inmediatos en ProductList.js).
        setFavoriteIds((current) => {
            const next = new Set(current);
            if (isFavorite) {
                next.delete(dealId);
            } else {
                next.add(dealId);
            }
            return next;
        });
        setFavorites((current) => (
            isFavorite
                ? current.filter((item) => item.id !== dealId)
                : [{ ...deal, addedAt: new Date().toISOString() }, ...current]
        ));

        const request = isFavorite
            ? fetch(`/api/favorites/${dealId}`, { method: 'DELETE', credentials: 'include' })
            : fetch('/api/favorites', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dealId })
            });

        request.then((response) => {
            if (!response.ok) {
                throw new Error('favorite-request-failed');
            }
        }).catch(() => refresh());
    }, [user, favoriteIds, refresh]);

    return (
        <FavoritesContext.Provider value={{ favorites, favoriteIds, toggleFavorite, refresh }}>
            {children}
            {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
        </FavoritesContext.Provider>
    );
};

export function useFavorites() {
    return useContext(FavoritesContext);
}
